import { useCallback, useEffect, useState } from "react";

import { Source } from "Constant/source";

import {
    getSourceZoho,
    sendSourceZohoData,
} from "helpers/Sources/createSource";

import SwalAlertComp from "components/SwalAlert";

import {
    EMPTY_LEAD_MAPPINGS,
    ZOHO_LEAD_MAPPING_KEYS,
} from "../SourceCreation.constants";
import { buildLeadMappings } from "../SourceCreation.helpers";

/**
 * Zoho CRM flow: pick a module, OAuth login, map lead fields, then report
 * the created source to the host.
 */
const useZohoSource = ({ sourceType, language, crmUsageIds, onCreated }) => {
    const [modules, setModules] = useState([]);
    const [selectedModule, setSelectedModule] = useState("");
    const [name, setName] = useState("");
    const [isModuleSelectDisabled, setIsModuleSelectDisabled] = useState(true);
    const [fields, setFields] = useState(null);
    const [sourceId, setSourceId] = useState("");
    const [leadMappings, setLeadMappings] = useState(EMPTY_LEAD_MAPPINGS);
    const [isLoading, setIsLoading] = useState(false);

    const failedTitle = language?.delete_swal_modal?.failed ?? "Failed";

    const reset = useCallback(() => {
        setSelectedModule("");
        setName("");
        setIsModuleSelectDisabled(true);
        setFields(null);
        setSourceId("");
        setLeadMappings(EMPTY_LEAD_MAPPINGS);
    }, []);

    useEffect(() => {
        if (sourceType !== Source.zoho) return;
        const fetchModules = async () => {
            const response = await getSourceZoho();
            if (response.status) {
                setModules(response?.data?.modules ?? []);
            } else {
                SwalAlertComp({
                    title: failedTitle,
                    message: response?.message ?? "",
                    icon: "error",
                });
            }
        };
        fetchModules();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceType === Source.zoho]);

    useEffect(() => {
        if (fields?.length)
            setLeadMappings(buildLeadMappings(fields, ZOHO_LEAD_MAPPING_KEYS));
    }, [fields]);

    const handleModuleChange = (event, module) => setSelectedModule(module);

    const handleNameChange = (event) => {
        setName(event.target.value);
        setIsModuleSelectDisabled(false);
    };

    const handleLoginSuccess = async (data) => {
        if (!data) return;
        setIsLoading(true);
        const response = await sendSourceZohoData({
            code: data?.code,
            location: data?.location,
            account_server: data?.account_server,
            name: data?.name,
            source_type: data?.source_type,
            module: data?.module,
            usage_types: crmUsageIds,
        });
        if (response.status) {
            setFields(response?.data?.field_data?.fields ?? []);
            setSourceId(response?.data?.source?.id);
            setIsModuleSelectDisabled(true);
        } else {
            SwalAlertComp({
                title: failedTitle,
                message: response?.message ?? "",
                icon: "error",
            });
        }
        setIsLoading(false);
    };

    const handleMappingSuccess = async (response) => {
        if (!response.status) {
            SwalAlertComp({
                title: failedTitle,
                message: response?.message ?? "",
                icon: "error",
            });
            return;
        }
        setIsLoading(true);
        SwalAlertComp({
            title: language?.delete_swal_modal?.success ?? "Success",
            message: response.message && response.message,
            icon: "success",
            timer: 3000,
        });
        const createdSourceId = sourceId;
        reset();
        await onCreated({ sourceId: createdSourceId, sourceType: Source.zoho });
        setIsLoading(false);
    };

    return {
        modules,
        selectedModule,
        name,
        isModuleSelectDisabled,
        hasFields: !!fields?.length,
        sourceId,
        leadMappings,
        setLeadMappings,
        isLoading,
        handleModuleChange,
        handleNameChange,
        handleLoginSuccess,
        handleMappingSuccess,
        reset,
    };
};

export default useZohoSource;
